<template>
  <p-dialog 
    :visible="visible" 
    :modal="true" 
    :closable="false" 
    :showHeader="false" 
    class="product-modal-dialog"
    @hide="onCancel"
  >
    <div class="modal-content-wrapper">
      <!-- Close Button -->
      <button class="close-btn" @click="onCancel">
        <i class="pi pi-times"></i>
      </button>
      <div class="premium-glass" v-if="hasStandardSubscription">
        <div class="premium-glass-left">
            <span class="premium-glass-icon">👑</span>
            <div class="premium-glass-text">
            <div class="premium-glass-title">You are accessing a premium feature</div>
            <div class="premium-glass-sub">
                Get premium now
            </div>
            </div>
        </div>

          <!-- <a href="#premium" class="premium-glass-btn">Upgrade</a> -->
      </div>
      <div class="create-subscription-banner" v-if="!hasAnySubscription">
        <div class="create-subscription-banner-left">
          <span class="create-subscription-banner-icon">👑</span>

          <div class="create-subscription-banner-text">
            <div class="create-subscription-banner-title">You're trying to access a Paid Feature. Join Pro Trader Today</div>
            <!-- optional -->
            <!-- <div class="create-subscription-banner-sub">Upgrade to unlock premium tools</div> -->
          </div>
        </div>
        <!-- <a href="#premium" class="create-subscription-banner-btn">Upgrade</a> -->
      </div>
      <div v-if="pendingDiscount" class="referral-offer-banner">
        <!-- Shimmer overlay -->
        <div class="referral-offer-banner__shimmer"></div>

        <!-- Left: icon + badge -->
        <div class="referral-offer-banner__icon-col">
          <div class="referral-offer-banner__icon-ring">
            <span class="referral-offer-banner__icon-emoji">🎁</span>
          </div>
          <div class="referral-offer-banner__tag">REFERRAL REWARD</div>
        </div>

        <!-- Center: text -->
        <div class="referral-offer-banner__body">
          <div class="referral-offer-banner__title">
            <span class="referral-offer-banner__title-star">✦</span>
            Special Offer Unlocked!
            <span class="referral-offer-banner__title-star">✦</span>
          </div>
          <div class="referral-offer-banner__subtitle">
            You were referred — enjoy
            <span class="referral-offer-banner__amount">50% off on all plans</span>
            on your first payment. Discount applied automatically at checkout.
            <div class="referral-currency-note">
              * Note: This discount applies only to {{ pendingDiscount.currency }} currency plans.
            </div>
          </div>
        </div>

        <!-- Right: discount pill -->
        <div class="referral-offer-banner__pill">
          <span class="referral-offer-banner__pill-value">50%</span>
          <span class="referral-offer-banner__pill-label">OFF</span>
        </div>
      </div>
      <div v-if="isFirstSubscription" class="trial-banner">
        <div class="trial-banner__icon">🔒</div>
        <div class="trial-banner__text">
          <strong>No charge today.</strong>
          Your credit card will not be charged until the 14 days is over.
        </div>
      </div>

      <!-- Monthly/Annual Toggle -->
      <div class="toggle-container">
        <div class="toggle-wrapper">
          <button 
            :class="['toggle-btn', { active: billingCycle === 'monthly' }]" 
            @click="billingCycle = 'monthly'"
          >
            Monthly
          </button>
          <button 
            :class="['toggle-btn', { active: billingCycle === 'annual' }]" 
            @click="billingCycle = 'annual'"
          >
            Annual
          </button>
        </div>
      </div>

      <div class="cards-container">
        <!-- Dynamic Product Cards -->
        <div 
          v-for="(product, index) in filteredProducts" 
          :key="index"
          class="pricing-card"
        >
          <!-- Currency Dropdown -->
          <div class="card-currency-select">
            <p-dropdown
              v-model="product.selectedCurrency"
              :options="availableCurrencies"
              optionLabel="country"
              placeholder="Select Currency"
              class="currency-dropdown"
            >
              <template #value="slotProps">
                <div class="country-item" v-if="slotProps.value">
                  <img :src="`https://flagcdn.com/24x18/${slotProps.value.flagCode}.png`" class="flag-icon" />
                  <span>{{ slotProps.value.country }}</span>
                </div>
                <span v-else>
                  {{slotProps.placeholder}}
                </span>
              </template>
              <template #option="slotProps">
                <div class="country-item">
                  <img :src="`https://flagcdn.com/24x18/${slotProps.option.flagCode}.png`" class="flag-icon" />
                  <span>{{ slotProps.option.country }}</span>
                </div>
              </template>
            </p-dropdown>
          </div>

          <h2 class="card-title">{{ product.title }}</h2>
          <p class="card-subtitle">{{ product.subtitle }}</p>

          <div class="price-display" :class="{ 'has-discount': hasReferralDiscount(product) }">
            <!-- Normal price (no referral discount) -->
            <template v-if="!hasReferralDiscount(product)">
              <span class="currency-symbol">{{ product.selectedCurrency.symbol }}</span>
              <span class="amount">{{ getPrice(product) }}</span>
              <span class="period">{{ product.selectedCurrency.name }} / Month</span>
            </template>

            <!-- Referral discounted price — both in one row -->
            <template v-else>
              <div class="discount-price-wrapper">
                <div class="combined-price-row">
                  <span class="currency-symbol original-symbol">{{ product.selectedCurrency.symbol }}</span>
                  <span class="amount original-amount">{{ getPrice(product) }}</span>
                  <span class="price-separator"></span>
                  <span class="currency-symbol discounted-symbol">{{ product.selectedCurrency.symbol }}</span>
                  <span class="amount discounted-amount">{{ getDiscountedPrice(product) }}</span>
                  <span class="period">{{ product.selectedCurrency.name }} / Month</span>
                </div>
                <div class="referral-discount-badge">🎉 -{{ product.selectedCurrency.symbol }}{{ getDiscountAmount(product) }} referral discount</div>
              </div>
            </template>
          </div>

          <p class="whats-included">What's included:</p>
          <ul class="features-list">
            <li v-for="(feature, fIndex) in product.features" :key="fIndex">
              <i class="pi pi-check-circle"></i>
              <span v-html="feature"></span>
            </li>
          </ul>

          <div class="card-actions">
            <button 
              type="button" 
              class="action-btn primary-btn" 
              @click="subscribe(product)"
            >
              {{ product.ctaPrimary }} <i class="pi pi-arrow-right"></i>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Error Dialog -->
    <p-dialog :visible.sync="showErrorDialog" :modal="true" :closable="false" header="Notice" :style="{width: '350px'}">
      <div class="p-d-flex p-ai-center p-dir-col">
        <i class="pi pi-info-circle p-text-center" style="font-size: 3rem; color: var(--primary-color); margin-bottom: 1rem;"></i>
        <p class="p-text-center" style="font-size: 1.1rem; line-height: 1.5; color: #000;">{{ msg }}</p>
      </div>
      <template #footer>
        <div class="p-d-flex p-jc-center">
          <p-button label="OK" @click="showErrorDialog = false" autofocus />
        </div>
      </template>
    </p-dialog>
  </p-dialog>
</template>

<script>
import { mapGetters, mapMutations } from 'vuex'
import { subscribeToPackage, getPendingReferralDiscount } from '../../../services/user'

export default {
  name: 'ProductModal',
  props: {
    visible: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      billingCycle: 'monthly',
      msg: '',
      showErrorDialog: false,
      isFirstSubscription: false,
      pendingDiscount: null,
      currencies: [
        { name: 'USD', symbol: '$', code: 'USD', country: 'United States', flagCode: 'us' },
        { name: 'AUD', symbol: '$', code: 'AUD', country: 'Australia', flagCode: 'au' },
      ],
      products: [
        {
          id: 'pro_noasx',
          title: 'USA Only',
          subtitle: 'ProTrader® with live US stocks only.',
          features: [
            'Unlimited indicators',
            'Unlimited access to our proprietary scanners',
            'Live data for all NYSE and NASDAQ stocks <br> &nbsp;&nbsp;&nbsp;• Intraday charts down to 1-minute bars',
            'Full access to our Exclusive Trading Room',
            'Learn with Protrader Academy',
            'Up to 4 charts per tab',
            'Fast full support',
            'Up to 25 years of historical data'
          ],
          ctaPrimary: 'Get USA Only Now',
          selectedCurrency: { name: 'USD', symbol: '$', code: 'USD', country: 'United States', flagCode: 'us' },
          pricing: {
            USD: { 
              monthly: 19.00,
              yearly: 17.41, 
              monthlyPlanId: 'monthly_noASX', 
              yearlyPlanId: 'yearly_noASX' 
            },
            AUD: { 
              monthly: 25.99, 
              yearly: 23.75, 
              monthlyPlanId: 'monthly_noASX', 
              yearlyPlanId: 'yearly_noASX' 
            }
          }
        },
        {
          id: 'pro',
          title: 'Premium',
          subtitle: 'ProTrader® software was developed by professional traders to allow users to find hot stocks in seconds.',
          features: [
            'Unlimited Indicators',
            'Unlimited access to our proprietary scanners',
            'Live data for all NYSE and NASDAQ stocks <br> &nbsp;&nbsp;&nbsp;• Intraday charts down to 1-minute bars',
            'End of day data for ASX, SGX and TSX stocks <br> &nbsp;&nbsp;&nbsp;• ASX updates hourly during the trading day',
            'Full access to our Exclusive Trading Room',
            'Learn with Protrader Academy',
            'Up to 4 charts per tab',
            'Fast full support',
            'Up to 25 years of historical data'
          ],
          ctaPrimary: 'Get Premium Now',
          selectedCurrency: { name: 'USD', symbol: '$', code: 'USD', country: 'United States', flagCode: 'us' },
          pricing: {
            USD: { 
              monthly: 49.00, 
              yearly: 44.92, 
              monthlyPlanId: 'monthly', 
              yearlyPlanId: 'yearly' 
            },
            AUD: { 
              monthly: 69.00, 
              yearly: 63.25, 
              monthlyPlanId: 'monthly', 
              yearlyPlanId: 'yearly' 
            }
          }
        }
      ]
    }
  },
  computed: {
    ...mapGetters(['getUserId']),
    hasStandardSubscription() {
      const subs = this.$store.state.user.subscriptionList || []
      return subs.some(sub => {
        const title = sub.product?.desc?.title || ''
        return title.includes('Standard')
      })
    },
    hasAnySubscription() {
      const hasSub = this.$store.state.user.subscriptionList
      const subs = hasSub.length>0 && hasSub[0].message === "Subscription is not found" ? [] : hasSub
      this.isFirstSubscription = this.$store.state.user.subscriptionList.length !== 0 && this.$store.state.user.subscriptionList[0]?.message === "Subscription is not found" && subs.response?.response?.statusCode === 404 ? true : false
      return subs.length > 0
    },
    filteredProducts() {
      if (this.hasStandardSubscription) {
        return this.products.filter(product => product.id !== 'pro_noasx');
      }
      return this.products;
    },
    availableCurrencies() {
      if (this.pendingDiscount && this.pendingDiscount.currency) {
        const refCurrency = this.pendingDiscount.currency.toUpperCase();
        return this.currencies.filter(c => c.code.toUpperCase() === refCurrency);
      }
      return this.currencies;
    }
  },
  methods: {
    ...mapMutations(['SET_RESTRICT_POPUP']),
    onCancel() {
      this.SET_RESTRICT_POPUP(false)
    },
    getPrice(product) {
      const currencyCode = product.selectedCurrency.code;
      const priceObj = product.pricing[currencyCode];
      if (!priceObj) return 'N/A';
      return this.billingCycle === 'monthly' ? priceObj.monthly : priceObj.yearly;
    },
    hasReferralDiscount(product) {
      return !!(
        this.pendingDiscount &&
        product.selectedCurrency.code === this.pendingDiscount.currency
      );
    },
    getDiscountAmount(product) {
      if (!this.pendingDiscount) return 0;
      if (product.id === 'pro' || product.id === 'pro_noasx') {
        const basePrice = parseFloat(this.getPrice(product));
        return isNaN(basePrice) ? 0 : (basePrice * 0.5).toFixed(2).replace(/\.00$/, '');
      }
      return this.pendingDiscount.amount;
    },
    getDiscountedPrice(product) {
      const basePrice = parseFloat(this.getPrice(product));
      if (isNaN(basePrice)) return 0;
      const discount = parseFloat(this.getDiscountAmount(product));
      return Math.max(0, basePrice - discount).toFixed(2).replace(/\.00$/, '');
    },
    async subscribe(product) {
      const currencyCode = product.selectedCurrency.code;
      const priceObj = product.pricing[currencyCode];
      let planId = this.billingCycle === 'monthly' ? priceObj.monthlyPlanId : priceObj.yearlyPlanId;
      const isUpgrade = this.hasStandardSubscription ? 'true' : 'false';
      
      const url = await subscribeToPackage(planId, currencyCode, isUpgrade);
      
      if (url && url.error) {
        this.msg = url.message;
        this.showErrorDialog = true;
      } else if (url.data && typeof url.data === 'string') {
        window.location = url.data;
      } else if (url.data && url.data.isUpgrade) {
        window.location = '/';
      }
    },
    async fetchDiscount() {
      try {
        const res = await getPendingReferralDiscount();
        if (res && res.success && res.discount) {
          this.pendingDiscount = res.discount;
          const refCurrency = res.discount.currency.toUpperCase();
          const targetCurrencyObj = this.currencies.find(c => c.code.toUpperCase() === refCurrency);
          if (targetCurrencyObj) {
            this.products.forEach(product => {
              product.selectedCurrency = targetCurrencyObj;
            });
          }
        }
      } catch (err) {
        console.error('Error fetching discount', err);
      }
    },
  },
  mounted() {
    this.fetchDiscount()
  }
}
</script>

<style scoped>
:deep(.p-dialog.product-modal-dialog) {
    background: #fff !important;
    border: 1px solid #ddd;
    border-radius: 20px;
    box-shadow: 0 10px 40px rgba(0,0,0,0.2);
    overflow: hidden;
    width: 70rem;
}

:deep(.p-dialog-content) {
    background: #fff !important;
    padding: 0 !important;
}

.modal-content-wrapper {
  background-color: #fff;
  color: #000;
  padding: 1rem 2rem;
  position: relative;
  min-height: 500px;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.close-btn {
  position: absolute;
  top: 1.5rem;
  right: 1.5rem;
  background: transparent;
  border: none;
  color: #000;
  font-size: 1.5rem;
  cursor: pointer;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 5px;
  transition: opacity 0.2s;
}

.close-btn:hover {
  opacity: 0.7;
}

.toggle-container {
  display: flex;
  justify-content: center;
  margin-bottom: 1.5rem;
  margin-top: 1rem;
  width: 100%;
}

.toggle-wrapper {
  background: #f0f0f0;
  border: 1px solid #ccc;
  border-radius: 50px;
  padding: 5px;
  display: flex;
  gap: 5px;
}

.toggle-btn {
  background: transparent;
  border: none;
  color: #666;
  padding: 6px 15px;
  border-radius: 50px;
  cursor: pointer;
  font-weight: 700;
  font-size: 1rem;
  transition: all 0.3s ease;
}

.toggle-btn.active {
  background: #fff;
  color: #db2828;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
}

.cards-container {
  display: flex;
  gap: 2rem;
  flex-wrap: wrap;
  justify-content: center;
  width: 100%;
}

.pricing-card {
  background: #fff;
  border: 1px solid #eee;
  box-shadow: 0 5px 20px rgba(0,0,0,0.05);
  border-radius: 24px;
  padding: 1rem;
  width: 100%;
  max-width: 368px;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.card-currency-select {
  width: 100%;
  display: flex;
  justify-content: flex-end;
  margin-bottom: 1rem;
  
}
.currency-dropdown {
  background: #f0f0f0;
}

.card-title {
  font-size: 2rem;
  font-weight: 800;
  margin-top: 0;
  margin-bottom: 0.5rem;
  text-align: center;
  text-transform: uppercase;
  color: #000;
}

.card-subtitle {
  color: #666;
  font-size: 1rem;
  text-align: center;
  margin-bottom: 1rem;
}

.price-display {
  display: flex;
  align-items: baseline;
  justify-content: center;
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.price-display.has-discount {
  flex-direction: column;
  align-items: center;
  gap: 4px;
}

.discount-price-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}

.combined-price-row {
  display: flex;
  align-items: baseline;
  gap: 0.35rem;
  flex-wrap: nowrap;
}

.original-symbol {
  color: #aaa;
  opacity: 0.6;
  font-size: 1.2rem;
}

.original-amount {
  color: #aaa;
  text-decoration: line-through;
  opacity: 0.55;
  font-size: 1.8rem;
}

.price-separator {
  width: 10px;
  display: inline-block;
}

.discounted-symbol {
  color: #059669;
  font-size: 1.5rem;
  font-weight: bold;
}

.discounted-amount {
  color: #059669 !important;
  text-shadow: 0 0 12px rgba(5, 150, 105, 0.3);
}

.referral-discount-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: rgba(5, 150, 105, 0.08);
  border: 1px solid rgba(5, 150, 105, 0.3);
  border-radius: 20px;
  padding: 4px 14px;
  font-size: 0.78rem;
  color: #047857;
  font-weight: 600;
  margin-top: 4px;
  letter-spacing: 0.02em;
  animation: badgePulse 2.5s ease-in-out infinite alternate;
}

@keyframes badgePulse {
  from { box-shadow: 0 0 0px rgba(5, 150, 105, 0.1); }
  to   { box-shadow: 0 0 8px rgba(5, 150, 105, 0.3); }
}

.currency-symbol {
  font-size: 1.5rem;
  font-weight: 600;
}

.amount {
  font-size: 3rem;
  font-weight: 900;
  line-height: 1;
}

.period {
  font-size: 1.2rem;
  color: #666;
}

.whats-included {
  font-weight: 800;
  margin-bottom: 0.8rem;
  font-size: 1.2rem;
  text-align: center;
}

.features-list {
  list-style: none;
  padding: 0 3rem;
  margin: 0 0 2rem 0;
  text-align: left;
}

.features-list li {
  font-size: 1rem;
  margin-bottom: 0.3rem;
  display: flex;
  align-items: baseline;
  gap: 9px;
  color: #333;
}

.features-list li i {
  color: #db2828;
  font-size: 1rem;
}

.card-actions {
  display: flex;
  justify-content: center;
  width: 100%;
}

.action-btn {
  background: #db2828;
  color: white;
  border: none;
  padding: 0.8rem 2rem;
  border-radius: 12px;
  font-weight: 800;
  font-size: 0.9rem;
  cursor: pointer;
  transition: transform 0.2s, background 0.2s;
  width: 100%;
  max-width: 270px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
}

.action-btn:hover {
  transform: scale(1.02);
  background: #b71c1c;
}

:deep(.p-dropdown) {
    border: none;
    border-radius: 20px;
    height: 35px;
    display: flex;
    align-items: center;
    padding: 0 10px;
}

:deep(.p-dropdown-label) {
    color: #000;
    font-weight: bold;
    font-size: 0.9rem;
}

:deep(.p-dropdown-panel) {
    background: #fff;
}

:deep(.p-dropdown-items .p-dropdown-item) {
    color: #000;
}

.country-item {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 0.8rem;
}

.flag-icon {
  width: 16px;
  height: 12px;
}

/* ===== Glass Premium Notice ===== */
.premium-glass {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;

  padding: 14px 18px;
  margin-bottom: 16px;

  border-radius: 14px;

  /* Glass effect */
  background: rgba(255, 255, 255, 0.55);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);

  border: 1px solid rgba(255, 255, 255, 0.6);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
}

/* ===== Glass Premium Notice ===== */
.premium-glass {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;

  padding: 14px 18px;
  margin-bottom: 16px;

  border-radius: 14px;

  /* Glass effect */
  background: rgba(255, 255, 255, 0.55);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);

  border: 1px solid rgba(255, 255, 255, 0.6);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
}

/* Left content layout */
.premium-glass-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

/* Icon */
.premium-glass-icon {
  font-size: 18px;
  color: #F4B400;
}

/* Text styles */
.premium-glass-title {
  font-size: 14px;
  font-weight: 700;
  color: #222;
}

.premium-glass-sub {
  font-size: 12.5px;
  color: #555;
  margin-top: 2px;
}

/* Upgrade button (small, classy) */
.premium-glass-btn {
  background: #E53935;
  color: white;
  padding: 8px 14px;
  border-radius: 8px;
  text-decoration: none;
  font-size: 13px;
  font-weight: 600;
  transition: all 0.2s ease;
  white-space: nowrap;
}

.premium-glass-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 6px 14px rgba(229, 57, 53, 0.3);
}

.create-subscription-banner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 22px;

  padding: 18px 22px;
  /* margin-bottom: 18px; */
  border-radius: 16px;

  /* Premium glass gradient */
  background: linear-gradient(135deg, rgba(255,255,255,0.85), rgba(255,255,255,0.6));
  backdrop-filter: blur(14px);
  -webkit-backdrop-filter: blur(14px);

  border: 1px solid rgba(255,255,255,0.7);

  /* Depth */
  box-shadow:
    0 12px 30px rgba(0,0,0,0.08),
    inset 0 1px 0 rgba(255,255,255,0.9);

  position: relative;
  overflow: hidden;
  transition: all 0.25s ease;
}

/* subtle glow accent */
.create-subscription-banner::before {
  content: "";
  position: absolute;
  top: -40%;
  right: -10%;
  width: 260px;
  height: 260px;
  background: radial-gradient(circle, rgba(229,57,53,0.15) 0%, transparent 70%);
  pointer-events: none;
}

/* hover lift */
.create-subscription-banner:hover {
  transform: translateY(-2px);
  box-shadow:
    0 16px 36px rgba(0,0,0,0.12),
    inset 0 1px 0 rgba(255,255,255,0.9);
}

/* layout */
.create-subscription-banner-left {
  display: flex;
  align-items: center;
  gap: 14px;
}

/* icon styling */
.create-subscription-banner-icon {
  font-size: 20px;
  background: linear-gradient(135deg, #ffb300, #ff7043);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  filter: drop-shadow(0 3px 6px rgba(255,160,0,0.35));
}

/* title */
.create-subscription-banner-title {
  font-size: 15px;
  font-weight: 700;
  color: #111;
  letter-spacing: 0.2px;
}

/* optional subtext */
.create-subscription-banner-sub {
  font-size: 13px;
  color: #666;
  margin-top: 3px;
}

/* CTA button */
.create-subscription-banner-btn {
  background: linear-gradient(135deg, #E53935, #FF7043);
  color: white;
  padding: 10px 16px;
  border-radius: 10px;
  text-decoration: none;
  font-size: 13px;
  font-weight: 600;

  box-shadow: 0 8px 18px rgba(229,57,53,0.35);
  transition: all 0.25s ease;
  white-space: nowrap;
}

.create-subscription-banner-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 12px 26px rgba(229,57,53,0.45);
}


.trial-banner {
  margin: 20px auto 10px;
  max-width: 720px;
  padding: 14px 20px;

  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;

  background: rgba(252, 252, 252, 0.12);
  border: 1px solid rgba(76, 189, 47, 0.4);
  border-radius: 12px;

  color: #0f954d;
  font-size: 15px;
  line-height: 1.4;

  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);

  box-shadow: 
    0 0 0 rgba(217, 29, 29, 0),
    0 8px 24px rgba(10, 213, 10, 0.4);

  animation: trialFadeIn 0.6s ease-out,
             trialGlow 2.5s ease-in-out infinite alternate;
}

.trial-banner__icon {
  font-size: 18px;
  flex-shrink: 0;
}

.trial-banner__text strong {
  color: #016e00;
  font-weight: 600;
}

/* Entrance animation */
@keyframes trialFadeIn {
  from {
    opacity: 0;
    transform: translateY(-6px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Subtle glowing effect */
@keyframes trialGlow {
  from {
    box-shadow:
      0 0 0 rgba(43, 234, 139, 0.15),
      0 8px 24px rgba(176, 227, 170, 0.4);
  }
  to {
    box-shadow:
      0 0 14px rgba(60, 220, 140, 0.35),
      0 8px 24px rgba(30, 173, 14, 0.4);
  }
}

/* ── Premium Referral Offer Banner (light modal theme) ── */
.referral-offer-banner {
  position: relative;
  overflow: hidden;

  display: flex;
  align-items: center;
  gap: 16px;

  width: 100%;
  margin: 10px auto 0 auto;
  padding: 12px 18px;

  background: linear-gradient(135deg, #f0fdf8 0%, #ecfdf5 50%, #f0fdf8 100%);
  border: 1px solid rgba(16, 185, 129, 0.30);
  border-radius: 14px;

  box-shadow:
    0 0 0 1px rgba(16, 185, 129, 0.08),
    0 4px 16px rgba(0, 0, 0, 0.06),
    0 0 28px rgba(16, 185, 129, 0.08);

  animation: offerBannerIn 0.5s cubic-bezier(0.22, 1, 0.36, 1) both,
             offerBannerGlow 3s ease-in-out infinite alternate;
}

.referral-offer-banner__shimmer {
  position: absolute;
  inset: 0;
  background: linear-gradient(
    110deg,
    transparent 20%,
    rgba(16, 185, 129, 0.10) 50%,
    transparent 80%
  );
  background-size: 200% 100%;
  animation: offerShimmer 3.5s linear infinite;
  pointer-events: none;
  border-radius: inherit;
}

.referral-offer-banner__icon-col {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
}

.referral-offer-banner__icon-ring {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: radial-gradient(circle at 40% 35%, #d1fae5, #a7f3d0);
  border: 2px solid rgba(16, 185, 129, 0.50);
  box-shadow: 0 0 12px rgba(16, 185, 129, 0.25);
  font-size: 18px;
  animation: iconPulse 2.5s ease-in-out infinite alternate;
}

.referral-offer-banner__icon-emoji {
  display: block;
  line-height: 1;
  animation: iconBounce 2.5s ease-in-out infinite;
}

.referral-offer-banner__tag {
  background: rgba(16, 185, 129, 0.12);
  border: 1px solid rgba(16, 185, 129, 0.35);
  border-radius: 20px;
  padding: 2px 8px;
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.08em;
  color: #059669;
  white-space: nowrap;
}

.referral-offer-banner__body {
  flex: 1;
  min-width: 0;
}

.referral-offer-banner__title {
  font-size: 14px;
  font-weight: 800;
  color: #064e3b;
  letter-spacing: 0.01em;
  margin-bottom: 3px;
  display: flex;
  align-items: center;
  gap: 6px;
}

.referral-offer-banner__title-star {
  color: #d97706;
  font-size: 12px;
  opacity: 0.9;
  animation: starSpin 4s linear infinite;
}

.referral-offer-banner__subtitle {
  font-size: 12px;
  color: #4b7563;
  line-height: 1.4;
}

.referral-currency-note {
  font-size: 11px;
  color: #b45309;
  font-weight: 700;
  margin-top: 6px;
}

.referral-offer-banner__amount {
  font-weight: 700;
  color: #047857;
  background: rgba(16, 185, 129, 0.12);
  padding: 1px 6px;
  border-radius: 5px;
  border: 1px solid rgba(16, 185, 129, 0.30);
  font-size: 12px;
  white-space: nowrap;
}

.referral-offer-banner__pill {
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-width: 65px;
  padding: 8px 10px;
  border-radius: 10px;
  background: linear-gradient(145deg, #059669, #047857);
  border: 1.5px solid rgba(16, 185, 129, 0.6);
  box-shadow: 0 0 14px rgba(16, 185, 129, 0.25), inset 0 1px 0 rgba(255,255,255,0.15);
  position: relative;
  z-index: 1;
  animation: pillPulse 2.5s ease-in-out infinite alternate;
}

.referral-offer-banner__pill-minus {
  font-size: 14px;
  font-weight: 900;
  color: #a7f3d0;
  line-height: 1;
  margin-bottom: -2px;
}

.referral-offer-banner__pill-value {
  font-size: 13px;
  font-weight: 900;
  color: #ffffff;
  line-height: 1.1;
  letter-spacing: -0.01em;
}

.referral-offer-banner__pill-label {
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.12em;
  color: #d1fae5;
  margin-top: 2px;
}

@keyframes offerBannerIn {
  from { opacity: 0; transform: translateY(-8px) scale(0.98); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
}

@keyframes offerBannerGlow {
  from { box-shadow: 0 0 0 1px rgba(16,185,129,0.08), 0 4px 16px rgba(0,0,0,0.06), 0 0 28px rgba(16,185,129,0.07); }
  to   { box-shadow: 0 0 0 1px rgba(16,185,129,0.18), 0 4px 16px rgba(0,0,0,0.06), 0 0 40px rgba(16,185,129,0.14); }
}

@keyframes offerShimmer {
  0%   { background-position: -200% 0; }
  100% { background-position:  200% 0; }
}

@keyframes iconPulse {
  from { box-shadow: 0 0 8px rgba(16,185,129,0.20); }
  to   { box-shadow: 0 0 18px rgba(16,185,129,0.45); }
}

@keyframes iconBounce {
  0%, 100% { transform: translateY(0); }
  45%, 55% { transform: translateY(-3px); }
}

@keyframes pillPulse {
  from { box-shadow: 0 0 10px rgba(16,185,129,0.20), inset 0 1px 0 rgba(255,255,255,0.15); }
  to   { box-shadow: 0 0 22px rgba(16,185,129,0.40), inset 0 1px 0 rgba(255,255,255,0.20); }
}

@keyframes starSpin {
  0%   { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

</style>
